import type { RunState } from '../api/types';

/**
 * Builds a render model (a grid of tiles) from the authoritative run state. Pure,
 * so the placeholder dungeon view stays a thin projection of server state.
 */

export type TileMarker = 'player' | 'enemy' | 'gold' | 'item' | 'ferry' | 'stair';

export interface Tile {
  x: number;
  y: number;
  markers: TileMarker[];
  /** Compact glyph for the top marker (player wins, then enemy, etc.). */
  glyph: string;
  label: string;
}

const GLYPHS: Record<TileMarker, string> = {
  player: '@',
  enemy: 'e',
  ferry: 'F',
  stair: '>',
  gold: '$',
  item: '!',
};

const PRIORITY: TileMarker[] = ['player', 'enemy', 'ferry', 'stair', 'item', 'gold'];

const samePos = (a: { x: number; y: number }, b: { x: number; y: number } | null | undefined) =>
  b != null && a.x === b.x && a.y === b.y;

export function buildGrid(state: RunState): Tile[][] {
  const { width, height, ferryStep, descentStair, enemies, goldCaches, itemDrops } = state.floorState;
  const rows: Tile[][] = [];
  for (let y = 0; y < height; y += 1) {
    const row: Tile[] = [];
    for (let x = 0; x < width; x += 1) {
      const pos = { x, y };
      const markers: TileMarker[] = [];
      if (samePos(state.position, pos)) markers.push('player');
      if (enemies.some((e) => e.hp > 0 && samePos(e.position, pos))) markers.push('enemy');
      if (ferryStep && samePos(ferryStep, pos)) markers.push('ferry');
      if (samePos(descentStair, pos)) markers.push('stair');
      if (itemDrops.some((d) => d.item.state === 'found' && samePos(d.position, pos))) markers.push('item');
      if (goldCaches.some((g) => !g.collected && samePos(g.position, pos))) markers.push('gold');

      const top = PRIORITY.find((m) => markers.includes(m));
      row.push({
        x,
        y,
        markers,
        glyph: top ? GLYPHS[top] : '·',
        label: markers.length > 0 ? markers.join(', ') : 'empty',
      });
    }
    rows.push(row);
  }
  return rows;
}

/** Short legend describing every marker glyph (for the UI footer). */
export const LEGEND: Array<{ glyph: string; label: string }> = [
  { glyph: '@', label: 'you' },
  { glyph: 'e', label: 'enemy' },
  { glyph: 'F', label: 'ferry (extract)' },
  { glyph: '>', label: 'descent stair' },
  { glyph: '$', label: 'gold cache' },
  { glyph: '!', label: 'item drop' },
];
